<template>
  <div ref="chartContainer" class="container-wrapper" :style="{ width: width, height: height }"></div>
</template>

<script>
import Plotly from 'plotly.js-dist';
import { RandomId } from '@/shared/lib/random';

const { generateRandomId } = RandomId();

export default {
  props: {
    /**
     * The Plotly options object containing data, layout, and config.
     * Refer to https://plotly.com/javascript/reference/ for full details.
     * @type {object}
     * @default { data: [], layout: {}, config: {} }
     */
    options: {
      type: Object,
      default: () => ({
        data: [],
        layout: {},
        config: {}
      })
    },
    /**
     * The width of the chart container.
     * Can be any valid CSS width value (e.g., '100%', '800px').
     * @type {string}
     * @default '100%'
     */
    width: {
      type: String,
      default: '100%'
    },
    /**
     * The height of the chart container.
     * Can be any valid CSS height value (e.g., '400px', '50vh').
     * @type {string}
     * @default '400px'
     */
    height: {
      type: String,
      default: '400px'
    }
  },
  data() {
    return {
      chartId: this.generateRandomId('plotly-chart-'),
      plotlyInstance: null,
      lastRenderedOptions: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.renderChart();
    });
  },
  beforeDestroy() {
    if (this.plotlyInstance) {
      try {
        Plotly.purge(this.$refs.chartContainer);
      } catch (error) {
        console.error('Plotly chart cleanup error during destroy:', error);
      } finally {
        this.plotlyInstance = null;
      }
    }
  },
  watch: {
    options: {
      handler(newOptions) {
        if (JSON.stringify(newOptions) === JSON.stringify(this.lastRenderedOptions)) {
          return;
        }

        if (!this.isValidPlotlyOptions(newOptions)) {
          console.warn('Invalid Plotly options received. Skipping chart update.');
          if (this.plotlyInstance) {
            try { Plotly.purge(this.$refs.chartContainer); } catch (e) { /* ignore error */ }
            this.plotlyInstance = null;
          }
          this.lastRenderedOptions = null;
          return;
        }

        if (!this.plotlyInstance) {
          this.renderChart();
        } else {
          const configChanged = JSON.stringify(newOptions.config) !== JSON.stringify(this.plotlyInstance.layout._inferredConfig);

          if (configChanged) {
            this.recreateChart();
          } else {
            this.updateChart();
          }
        }
      },
    },
    width() { this.resize(); },
    height() { this.resize(); }
  },
  methods: {
    generateRandomId,

    /**
     * Validates the essential Plotly options (data, layout, config).
     * @param {object} options - The Plotly options object.
     * @returns {boolean} True if options are valid for Plotly rendering, false otherwise.
     */
    isValidPlotlyOptions(options) {
      if (!options || typeof options !== 'object') return false;
      if (!Array.isArray(options.data)) return false;
      if (typeof options.layout !== 'object' || options.layout === null) return false;
      return !(typeof options.config !== 'object' || options.config === null);

    },

    /**
     * Renders/creates the Plotly chart instance from scratch.
     * This method is called for initial render, full re-creation, or error recovery.
     */
    renderChart() {
      const element = this.$refs.chartContainer;

      if (!element || !this.isValidPlotlyOptions(this.options)) {
        return;
      }

      if (!element.id) {
        element.id = this.chartId;
      }

      try {
        Plotly.newPlot(element, this.options.data, this.options.layout, this.options.config)
          .then(plot => {
            this.plotlyInstance = plot;
            this.lastRenderedOptions = JSON.parse(JSON.stringify(this.options));
            /**
             * @event chart-created
             * @description Emitted when the Plotly chart has been successfully created.
             * @property {object} plot - The Plotly plot instance.
             */
            this.$emit('chart-created', plot);
          })
          .catch(error => {
            console.error('Error creating Plotly chart:', error);
            this.plotlyInstance = null;
            this.lastRenderedOptions = null;
            /**
             * @event chart-error
             * @description Emitted if there's an error during chart creation or update.
             * @property {Error} error - The error object.
             */
            this.$emit('chart-error', error);
          });
      } catch (error) {
        console.error('Synchronous error during Plotly.newPlot call:', error);
        this.$emit('chart-error', error);
      }
    },

    /**
     * Updates the existing Plotly chart with new data or layout using Plotly.react.
     * This is more efficient than recreating the entire chart.
     */
    updateChart() {
      const element = this.$refs.chartContainer;

      if (!this.plotlyInstance || !element || !this.isValidPlotlyOptions(this.options)) {
        console.warn('Skipping Plotly chart update due to missing instance or invalid options. Attempting full render instead.');
        this.renderChart();
        return;
      }

      try {
        Plotly.react(element, this.options.data, this.options.layout, this.options.config)
          .then(plot => {
            this.plotlyInstance = plot;
            this.lastRenderedOptions = JSON.parse(JSON.stringify(this.options));
            /**
             * @event chart-updated
             * @description Emitted when the Plotly chart has been successfully updated.
             * @property {object} plot - The Plotly plot instance.
             */
            this.$emit('chart-updated', plot);
          })
          .catch(error => {
            console.error('Error updating Plotly chart:', error);
            this.recreateChart();
            this.$emit('chart-error', error);
          });
      } catch (error) {
        console.error('Synchronous error during Plotly.react call:', error);
        this.$emit('chart-error', error);
      }
    },

    /**
     * Purges the existing chart and then creates a new one.
     * This is useful when `config` options change or for recovering from certain errors.
     */
    recreateChart() {
      if (this.plotlyInstance && this.$refs.chartContainer) {
        try {
          Plotly.purge(this.$refs.chartContainer);
        } catch (error) {
          console.error('Error purging chart before recreation:', error);
        } finally {
          this.plotlyInstance = null;
        }
      }
      this.$nextTick(() => {
        this.renderChart();
      });
    },

    /**
     * Resizes the Plotly chart to fit its container.
     * Plotly's `responsive: true` config usually handles this, but this method provides explicit control.
     */
    resize() {
      if (this.plotlyInstance && this.$refs.chartContainer) {
        try {
          Plotly.Plots.resize(this.$refs.chartContainer);
        } catch (error) {
          console.error('Error resizing Plotly chart:', error);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.container-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 1;
}
</style>
